import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

// Define the API URL - use environment variable or fallback to Render URL
const API_URL = process.env.REACT_APP_API_URL || 'https://mwendwa-kitavi-family-tree-backend-production.up.railway.app';

const PersonForm = ({ people, onUpdate, personToEdit, setShowEditForm, onCancel = () => {} }) => {
  const [personType, setPersonType] = useState(personToEdit?.personType || 'child');
  const [name, setName] = useState(personToEdit?.name || '');
  const [gender, setGender] = useState(personToEdit?.gender || 'MALE');
  const [birthDate, setBirthDate] = useState(personToEdit?.birthDate?.split('-')[0] || '');
  const [status, setStatus] = useState(personToEdit?.status || 'ALIVE');
  const [role, setRole] = useState(personToEdit?.role || 'REGULAR');
  const [fatherId, setFatherId] = useState(personToEdit?.father?._id || '');
  const [motherId, setMotherId] = useState(personToEdit?.mother?._id || '');
  const [socialParentId, setSocialParentId] = useState(personToEdit?.socialParent?._id || '');
  const [adoptiveFatherId, setAdoptiveFatherId] = useState(personToEdit?.adoptiveFather?._id || '');
  const [spouseOfId, setSpouseOfId] = useState(personToEdit?.spouses?.[0]?._id || '');
  const [birthOrder, setBirthOrder] = useState(personToEdit?.birthOrder || '');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(true);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const CORRECT_PASSWORD = 'Myname12@';

  const availablePeople = people.filter((person) => person._id !== personToEdit?._id);
  const allFathers = availablePeople.filter((person) => person.gender === 'MALE');
  const allMothers = availablePeople.filter((person) => person.gender === 'FEMALE');

  // A 'child' must belong to a recorded couple — once one parent is
  // picked, only show the OTHER parent options who are actually that
  // person's recorded spouse. This makes it structurally hard to pick a
  // father and mother who were never married/linked to each other.
  const selectedFatherPerson = fatherId ? people.find((p) => p._id === fatherId) : null;
  const selectedMotherPerson = motherId ? people.find((p) => p._id === motherId) : null;
  const fatherSpouseIds = new Set((selectedFatherPerson?.spouses || []).map((s) => s._id || s));
  const motherSpouseIds = new Set((selectedMotherPerson?.spouses || []).map((s) => s._id || s));

  const potentialFathers = selectedMotherPerson
    ? allFathers.filter((p) => motherSpouseIds.has(p._id))
    : allFathers;
  const potentialMothers = selectedFatherPerson
    ? allMothers.filter((p) => fatherSpouseIds.has(p._id))
    : allMothers;

  useEffect(() => {
    if (personToEdit) {
      setPersonType(personToEdit.personType || 'child');
      setName(personToEdit.name || '');
      setGender(personToEdit.gender || 'MALE');
      setBirthDate(personToEdit.birthDate ? personToEdit.birthDate.split('-')[0] : '');
      setStatus(personToEdit.status || 'ALIVE');
      setRole(personToEdit.role || 'REGULAR');
      setFatherId(personToEdit.father?._id || '');
      setMotherId(personToEdit.mother?._id || '');
      setSocialParentId(personToEdit.socialParent?._id || '');
      setAdoptiveFatherId(personToEdit.adoptiveFather?._id || '');
      setSpouseOfId(personToEdit.spouses?.[0]?._id || '');
      setBirthOrder(personToEdit.birthOrder || '');
    }
  }, [personToEdit]);

  const handlePasswordSubmit = () => {
    if (password === CORRECT_PASSWORD) {
      setOpenPasswordDialog(false);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  const handlePasswordKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePasswordSubmit();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name) {
      alert('Name is required.');
      return;
    }
    if (!gender) {
      alert('Gender is required.');
      return;
    }
    if (!status) {
      alert('Status is required.');
      return;
    }

    const newPerson = {
      name,
      gender,
      birthDate: birthDate || null,
      status,
      role,
      personType,
      birthOrder: birthOrder ? parseInt(birthOrder) : null,
    };

    // CRITICAL FIX: When editing, preserve ALL existing relationships
    if (personToEdit) {
      // Preserve children array - this should NEVER be lost during edit
      if (personToEdit.children && personToEdit.children.length > 0) {
        newPerson.children = personToEdit.children.map(c => c._id || c);
      }
      
      // Preserve existing spouses
      if (personToEdit.spouses && personToEdit.spouses.length > 0) {
        newPerson.spouses = personToEdit.spouses.map(s => s._id || s);
      }
      
      // Preserve existing kawetos
      if (personToEdit.kawetos && personToEdit.kawetos.length > 0) {
        newPerson.kawetos = personToEdit.kawetos.map(k => k._id || k);
      }
      
      // Preserve adoptedSpouses if any
      if (personToEdit.adoptedSpouses && personToEdit.adoptedSpouses.length > 0) {
        newPerson.adoptedSpouses = personToEdit.adoptedSpouses.map(s => s._id || s);
      }
      
      // Preserve existing parent relationships
      if (personToEdit.father) {
        newPerson.father = personToEdit.father._id || personToEdit.father;
      }
      if (personToEdit.mother) {
        newPerson.mother = personToEdit.mother._id || personToEdit.mother;
      }
      if (personToEdit.socialParent) {
        newPerson.socialParent = personToEdit.socialParent._id || personToEdit.socialParent;
      }
      if (personToEdit.adoptiveFather) {
        newPerson.adoptiveFather = personToEdit.adoptiveFather._id || personToEdit.adoptiveFather;
      }
    }

    // Now handle person type specific fields (these may override preserved values)
    if (personType === 'child') {
      if (!personToEdit && (!fatherId || !motherId)) {
        alert("A child needs both a father and a mother selected — they must already be recorded as spouses of each other. If you only know one parent, use \"Single-parent child\" instead.");
        return;
      }
      // Update parent fields if they were changed in the form
      if (fatherId || !personToEdit) {
        newPerson.father = fatherId || null;
      }
      if (motherId || !personToEdit) {
        newPerson.mother = motherId || null;
      }
    } else if (personType === 'spouse') {
      if (!personToEdit && !spouseOfId) {
        // Only require spouse selection for NEW spouses
        alert('Please select the person to whom this spouse will be linked.');
        return;
      }
      // For editing: merge new spouse with existing spouses
      if (spouseOfId) {
        const existingSpouses = newPerson.spouses || [];
        if (!existingSpouses.includes(spouseOfId)) {
          newPerson.spouses = [...existingSpouses, spouseOfId];
        }
      }
    } else if (personType === 'kaweto') {
      if (!personToEdit && !spouseOfId) {
        // Only require kaweto selection for NEW kawetos
        alert('Please select the person to whom this kaweto will be linked.');
        return;
      }
      // For editing: merge new kaweto with existing kawetos
      if (spouseOfId) {
        const existingKawetos = newPerson.kawetos || [];
        if (!existingKawetos.includes(spouseOfId)) {
          newPerson.kawetos = [...existingKawetos, spouseOfId];
        }
      }
    } else if (personType === 'single-parent-child') {
      if (!personToEdit && !socialParentId) {
        alert('Please select a social parent for the single-parent child.');
        return;
      }
      if (socialParentId) {
        newPerson.socialParent = socialParentId;
      }
    } else if (personType === 'adopted-adult-man') {
      if (!personToEdit && !adoptiveFatherId) {
        alert('Please select an adoptive father for the adopted adult man.');
        return;
      }
      if (adoptiveFatherId) {
        newPerson.adoptiveFather = adoptiveFatherId;
      }
    }

    try {
      if (personToEdit) {
        await axios.put(`${API_URL}/api/people/${personToEdit._id}`, newPerson);
      } else {
        const res = await axios.post(`${API_URL}/api/people`, newPerson);
        if (res.data?.pending) {
          alert(res.data.message);
        }
      }
      onUpdate();
      if (setShowEditForm) setShowEditForm(false);
      onCancel();
    } catch (err) {
      console.error('Error submitting form:', err.response ? err.response.data : err.message);
      alert('Error submitting form: ' + (err.response?.data?.error || err.response?.data?.message || err.message));
    }
  };

  return (
    <>
      <Dialog open={openPasswordDialog} onClose={() => { setOpenPasswordDialog(false); onCancel(); }}>
        <DialogTitle>Enter Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handlePasswordKeyPress}
            error={!!passwordError}
            helperText={passwordError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenPasswordDialog(false); onCancel(); }}>Cancel</Button>
          <Button onClick={handlePasswordSubmit} color="primary">Submit</Button>
        </DialogActions>
      </Dialog>
      {!openPasswordDialog && (
        <Box sx={{ padding: 2 }}>
          <Typography variant="h6" gutterBottom>
            {personToEdit ? 'Edit Person' : 'Add Person'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Person Type</InputLabel>
              <Select
                value={personType}
                onChange={(e) => setPersonType(e.target.value)}
                label="Person Type"
                disabled={!!personToEdit}
              >
                <MenuItem value="child">Child</MenuItem>
                <MenuItem value="spouse">Spouse</MenuItem>
                <MenuItem value="kaweto">Kaweto (Co-Wife)</MenuItem>
                <MenuItem value="single-parent-child">Single-Parent Child</MenuItem>
                <MenuItem value="adopted-adult-man">Adopted Adult Man</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Gender</InputLabel>
              <Select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                label="Gender"
                required
              >
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="Birth Year (Optional)"
              type="text"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              placeholder="e.g., 1990"
              helperText="Enter the birth year if known (optional)"
            />
            <TextField
              fullWidth
              margin="normal"
              label="Birth Order (Optional)"
              type="number"
              value={birthOrder}
              onChange={(e) => setBirthOrder(e.target.value)}
              placeholder="e.g., 1, 2, 3"
              helperText="Position among siblings: 1 = first-born (appears left), 2 = second-born, etc."
              inputProps={{ min: 1 }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Status"
                required
              >
                <MenuItem value="ALIVE">Alive</MenuItem>
                <MenuItem value="DEAD">Dead</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                label="Role"
                disabled={personType === 'kaweto'}
              >
                <MenuItem value="REGULAR">Regular</MenuItem>
                <MenuItem value="KAWETO">Kaweto</MenuItem>
              </Select>
            </FormControl>
            {personType === 'child' && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  A child must have both parents selected, and they must already be recorded as
                  spouses of each other. Add the spouse relationship first if it isn't there yet.
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Father</InputLabel>
                  <Select
                    value={fatherId}
                    onChange={(e) => setFatherId(e.target.value)}
                    label="Father"
                  >
                    <MenuItem value="">Select Father</MenuItem>
                    {potentialFathers.map((person) => (
                      <MenuItem key={person._id} value={person._id}>
                        {person.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedMotherPerson && potentialFathers.length === 0 && (
                    <Typography variant="caption" color="error">
                      {selectedMotherPerson.name} has no recorded spouse yet — add that first.
                    </Typography>
                  )}
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Mother</InputLabel>
                  <Select
                    value={motherId}
                    onChange={(e) => setMotherId(e.target.value)}
                    label="Mother"
                  >
                    <MenuItem value="">Select Mother</MenuItem>
                    {potentialMothers.map((person) => (
                      <MenuItem key={person._id} value={person._id}>
                        {person.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedFatherPerson && potentialMothers.length === 0 && (
                    <Typography variant="caption" color="error">
                      {selectedFatherPerson.name} has no recorded spouse yet — add that first.
                    </Typography>
                  )}
                </FormControl>
              </>
            )}
            {(personType === 'spouse' || personType === 'kaweto') && (
              <FormControl fullWidth margin="normal">
                <InputLabel>{personType === 'spouse' ? 'Spouse Of' : 'Kaweto Of'}</InputLabel>
                <Select
                  value={spouseOfId}
                  onChange={(e) => setSpouseOfId(e.target.value)}
                  label={personType === 'spouse' ? 'Spouse Of' : 'Kaweto Of'}
                  required
                >
                  <MenuItem value="">Select Person</MenuItem>
                  {availablePeople.map((person) => (
                    <MenuItem key={person._id} value={person._id}>
                      {person.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {personType === 'single-parent-child' && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Social Parent</InputLabel>
                <Select
                  value={socialParentId}
                  onChange={(e) => setSocialParentId(e.target.value)}
                  label="Social Parent"
                  required
                >
                  <MenuItem value="">Select Social Parent</MenuItem>
                  {availablePeople.map((person) => (
                    <MenuItem key={person._id} value={person._id}>
                      {person.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {personType === 'adopted-adult-man' && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Adoptive Father</InputLabel>
                <Select
                  value={adoptiveFatherId}
                  onChange={(e) => setAdoptiveFatherId(e.target.value)}
                  label="Adoptive Father"
                  required
                >
                  <MenuItem value="">Select Adoptive Father</MenuItem>
                  {potentialFathers.map((person) => (
                    <MenuItem key={person._id} value={person._id}>
                      {person.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box sx={{ marginTop: 2, display: 'flex', gap: 2 }}>
              <Button type="submit" variant="contained" color="primary">
                {personToEdit ? 'Update' : 'Add'}
              </Button>
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
            </Box>
          </form>
        </Box>
      )}
    </>
  );
};

export default PersonForm;